import React from 'react';
import { Button, Dropdown, Tooltip, type MenuProps } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/** One secondary row action. */
export interface RowAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  /** Destructive action: shown in red and separated from the others. */
  danger?: boolean;
  disabled?: boolean;
  /** Explains a disabled action on hover. */
  disabledReason?: string;
}

interface RowActionsMenuProps {
  actions: RowAction[];
  onSelect: (key: string) => void;
  loading?: boolean;
}

/** Width of the selection checkbox column, wide enough to sit centered. */
export const SELECTION_COLUMN_WIDTH = 56;

/**
 * A table row's secondary actions, shared by the Threads, Leads and Customers
 * lists. Several actions go into a "⋯" menu (destructive ones last, behind a
 * divider); a single action is shown directly as an icon button.
 */
export const RowActionsMenu: React.FC<RowActionsMenuProps> = ({ actions, onSelect, loading = false }) => {
  const { t } = useTranslation('manage');
  if (actions.length === 0) return null;

  if (actions.length === 1) {
    const [action] = actions;
    return (
      <Tooltip title={action.disabled && action.disabledReason ? action.disabledReason : action.label}>
        <Button
          size="small"
          icon={action.icon}
          danger={action.danger}
          disabled={action.disabled}
          loading={loading}
          aria-label={action.label}
          onClick={() => onSelect(action.key)}
        />
      </Tooltip>
    );
  }

  const regular = actions.filter((action) => !action.danger);
  const destructive = actions.filter((action) => action.danger);
  const toItem = (action: RowAction) => ({
    key: action.key,
    icon: action.icon,
    danger: action.danger,
    disabled: action.disabled,
    label:
      action.disabled && action.disabledReason ? (
        <Tooltip title={action.disabledReason} placement="left">
          <span>{action.label}</span>
        </Tooltip>
      ) : (
        action.label
      )
  });
  const items: MenuProps['items'] = [
    ...regular.map(toItem),
    ...(regular.length && destructive.length ? [{ type: 'divider' as const }] : []),
    ...destructive.map(toItem)
  ];

  return (
    <Dropdown trigger={['click']} menu={{ items, onClick: ({ key }) => onSelect(key) }}>
      <Button size="small" icon={<MoreOutlined />} loading={loading} aria-label={t('threads.table.moreActions')} />
    </Dropdown>
  );
};
